<template>
	<section class="basket card">

		<h2 class="title-basket" v-if="!insideStickyBasket">{{ basket.title }}</h2>

		<div class="content">

			<h3 class="title-monthly">Maandelijks</h3>

			<div class="main-product">
				<basket-product-line v-if="basket.mainProduct"
									 :compact="false"
									 :product="basket.mainProduct"
									 :show-discount-period-label="basket.mainProduct.discountApplicable"/>
			</div>

			<div class="monthly-costs">

				<basket-product-group
					v-for="block in basket.orderedProductsMonthly"
					:key="block.id"
					:basket-block="block"
				/>

			</div>

			<div class="total-monthly-cost">
				<basket-line
					v-for="item in basket.totalMonthlyCosts"
					:key="item.index"
					:highlight="item.action"
					:discount-price="item.totalPrice"
					:title="item.title"
					:price="item.originalPrice"
					compact
				/>
			</div>

			<div v-if="basket.orderedProductsOnce.length > 0" class="single-cost">
				<hr>

				<h3>Eenmalige kosten</h3>

				<basket-product-line
					v-for="product in basket.orderedProductsOnce"
					:key="product.internalKey"
					:product="product"
				/>
			</div>

			<slot></slot>

			<div v-if="isNotInsideStickyBasketAndNextStepTypeIsValid" class="cta">
				<div class="alert" v-if="basket.standingChargeProductToBeSelected">
					Je kunt pas verder wanneer je een selectie van de vastrechtvergoeding hebt gemaakt
				</div>

				<button v-if="basket.standingChargeProductToBeSelected && nextStep" class="button disabled">
					{{ nextStep.text }}
				</button>

				<template v-else>
					<button v-if="isNextStepTypeSubmit" :form="nextStep.form" class="button" type="submit">
						{{ nextStep.text }}
					</button>

					<a v-if="isNextStepTypeLink" :href="nextStep.url" class="button">
						{{ nextStep.text }}
					</a>
				</template>
			</div>

			<div v-if="basket.disclaimers" class="disclaimers">
				<p v-for="disclaimer in basket.disclaimers"
				   :key="disclaimer.id" v-html="disclaimer.text">
				</p>
			</div>
		</div>
	</section>

	<section>
		<div v-if="basket.contractBenefits" class="contract-benefits">
			<p v-for="contractBenefit in basket.contractBenefits"
			   :key="contractBenefit.order" class="benefits">
				<span class="material-icons">check</span>
				<span v-html="contractBenefit.text" class="benefits text"></span>
			</p>
		</div>
	</section>
</template>

<script lang="ts" setup>
	import BasketLine from '@/components/basket/BasketLine.vue';
	import BasketProductGroup from '@/components/basket/BasketProductGroup.vue';
	import BasketProductLine from '@/components/basket/BasketProductLine.vue';
	import type {Basket} from '@/types/basket';
	import type {NextStep} from '@/types/global-types';
	import {computed} from 'vue';

	const props = defineProps<{
		basket: Basket,
		nextStep: NextStep,
		insideStickyBasket?: boolean
	}>();

	const isNotInsideStickyBasketAndNextStepTypeIsValid = computed(() => {
		return !props.insideStickyBasket && props.nextStep.type;
	});

	const isNextStepTypeSubmit = computed(() => {
		return props.nextStep.type === 'SUBMIT';
	});

	const isNextStepTypeLink = computed(() => {
		return props.nextStep.type === 'LINK';
	});
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/icon?family=Material+Icons');

.card {
	padding-left: 16px;
	padding-right: 16px;
}

.basket {
	--padding-right: 28px;
	--basket-product-group-padding-x: 8px;
}

h2 {
	color: var(--color-primary_blue);
	font-weight: 700;
	margin: 0 0 24px;
	padding: 0;
}

h3 {
	color: #343434;
	font-weight: 700;
	margin: 0;
	padding: 0;
	font-size: var(--font-size_subtitle1);
	line-height: var(--font-line-height_subtitle1);
}

.main-product,
.monthly-costs,
.total-monthly-cost,
.single-cost,
.cta {
	margin-bottom: 16px;
}

.cta {
	margin: 32px 0;
}

.main-product,
.total-monthly-cost,
.single-cost,
.disclaimers,
.title-basket,
.title-monthly,
.cta {
	padding-left: 8px;
	padding-right: 8px;
}

.single-cost {
	hr {
		background: #dbdbdb;
		height: 1px;
		border: none;
		margin: 0 0 24px;
		padding: 0;
	}
}

.disclaimers {
	p {
		font: inherit;
		margin: 0 0 var(--font-line-height_body1);
		color: var(--color-secondary, #adadad);
	}
}

.contract-benefits {
	.benefits {
		font: inherit;
		margin: 0;
		vertical-align: top;
	}

	.benefits .material-icons {
		color: #7cb442;
		font-variation-settings: "FILL" 0, "wght" 500, "GRAD" 0, "opsz" 24;
		margin: 0 16px 0 8px;
		font-style: normal;
		font-size: 24px;
		line-height: 1;
		letter-spacing: normal;
		text-transform: none;
		display: inline-block;
		white-space: nowrap;
		word-wrap: normal;
		direction: ltr;
		font-feature-settings: "liga";
		-webkit-font-smoothing: antialiased;
	}
}
</style>
